import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TimerWidget = ({ projects, onTimeEntry, isFloating = false }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [selectedProject, setSelectedProject] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setSeconds(seconds => seconds + 1);
      }, 1000);
    } else if (!isRunning && seconds !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRunning, seconds]);

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleStart = () => {
    if (!selectedProject) return;
    setIsRunning(true);
    setStartTime(new Date());
  };

  const handleStop = () => {
    if (!selectedProject || !startTime) return;
    
    const endTime = new Date();
    const duration = (endTime - startTime) / 1000 / 3600; // Convert to hours
    
    onTimeEntry({
      projectId: selectedProject,
      task: taskDescription || 'Time tracking session',
      startTime: startTime?.toISOString(),
      endTime: endTime?.toISOString(),
      duration: duration,
      date: new Date()?.toISOString()?.split('T')?.[0]
    });

    setIsRunning(false);
    setSeconds(0);
    setTaskDescription('');
    setStartTime(null);
  };

  const handleReset = () => {
    setIsRunning(false);
    setSeconds(0);
    setStartTime(null);
  };

  const projectOptions = projects?.map(project => ({
    value: project?.id,
    label: `${project?.name} - ${project?.client}`
  }));

  const widgetClasses = isFloating
    ? "fixed bottom-6 right-6 w-80 bg-surface border border-border rounded-lg shadow-elevated z-50"
    : "w-full bg-surface border border-border rounded-lg";

  return (
    <div className={widgetClasses}>
      <div className="p-4">
        {/* Timer Display */}
        <div className="text-center mb-4">
          <div className="text-3xl font-mono font-bold text-foreground mb-2">
            {formatTime(seconds)}
          </div>
          <div className="text-sm text-muted-foreground">
            {isRunning ? 'Timer Running' : 'Timer Stopped'}
          </div>
        </div>

        {/* Project Selection */}
        <div className="mb-4">
          <Select
            label="Project"
            placeholder="Select a project"
            options={projectOptions}
            value={selectedProject}
            onChange={setSelectedProject}
            disabled={isRunning}
            required
          />
        </div>

        {/* Task Description */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="What are you working on?"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e?.target?.value)}
            disabled={isRunning}
            className="w-full px-3 py-2 border border-border rounded-md text-sm text-foreground placeholder-muted-foreground bg-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
        </div>

        {/* Timer Controls */}
        <div className="flex items-center justify-center space-x-2">
          {!isRunning ? (
            <Button
              variant="default"
              size="sm"
              iconName="Play"
              iconPosition="left"
              onClick={handleStart}
              disabled={!selectedProject}
              className="flex-1"
            >
              Start Timer
            </Button>
          ) : (
            <>
              <Button
                variant="destructive"
                size="sm"
                iconName="Square"
                iconPosition="left"
                onClick={handleStop}
                className="flex-1"
              >
                Stop
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                onClick={handleReset}
              >
                Reset
              </Button>
            </>
          )}
        </div>

        {/* Current Session Info */}
        {isRunning && selectedProject && (
          <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-md">
            <div className="text-sm">
              <div className="font-medium text-foreground mb-1">Current Session</div>
              <div className="text-muted-foreground">
                Project: {projects?.find(p => p?.id === selectedProject)?.name}
              </div>
              {taskDescription && (
                <div className="text-muted-foreground">Task: {taskDescription}</div>
              )}
              <div className="text-muted-foreground">
                Started: {startTime?.toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimerWidget;